import { useQuery } from '@apollo/client/react'
import { useEffect, useState } from 'react'
import {
  ICoordinates,
  ILocation,
  IQuery,
  IQueryDirectoryLocationsArgs,
  LocationType
} from 'src/types/graphql'
import { DIRECTORY_LOCATIONS } from './directoryQuery'

/**
 * How many directory rows one viewport fetches.
 *
 * ONE number, shared by the map layer and the tier floor on purpose: Apollo
 * dedupes by (query, variables), so identical variables mean the two surfaces
 * cost a single network request and the floor renders from cache the instant
 * it scrolls into view. Change this and they diverge into two requests.
 *
 * MUST equal the server's `MAX_DIRECTORY_PAGE_SIZE`
 * (spotme-server/src/api/directory/directory.resolver.ts) — the resolver
 * clamps silently, so a larger ask here just lies about the page size (the
 * pre-2026-08-24 value 72 was being clamped to 60 without anyone noticing).
 * 150 set with the confident-parking go-live; at metro zoom the server
 * returns a spatially uniform sample of the viewport rather than the
 * nearest-first blob.
 */
export const DIRECTORY_VIEWPORT_SIZE = 150

/**
 * Directory rows for a search viewport — the shared read behind BOTH the map
 * pins and the tier floor.
 *
 * ⚠️ Unlike the floor, the MAP consumes this eagerly: a map with no pins is
 * the whole problem it exists to fix, and in a market with no SpotMe supply
 * the directory is the only thing on it. That is a deliberate reversal of the
 * floor's lazy contract, and it is affordable because what became eager is one
 * cached GraphQL read — NOT the `DirectoryFloor` chunk, which stays
 * `ssr: false` and behind its scroll sentinel.
 *
 * The rows for the CURRENT viewport are held across a refetch of that same
 * viewport (2026-09-26). Until that day `ApolloProviderLoaded` built a new
 * client (empty cache) on every `_app` render, and a directory card's tap does
 * a shallow `Router.replace` before it navigates, so this `cache-first` query
 * re-ran with identical variables and came back as `loading` with no data: the
 * floor fell back to its 3-row skeleton, the sentinel dropped its reserved
 * height, the mobile drawer shrank by ~3,000px under a fixed scrollTop, and the
 * footer jumped into view twice before the ficha rendered (CLS 0.86 per jump).
 * The provider now keeps its client across shallow updates; this hold stays as
 * the cheap guard for any other same-mount refetch. A new viewport still
 * starts empty — only the same question keeps its answer.
 */
export function useDirectoryLocations(args: {
  center?: ICoordinates
  zoom?: number
  locationTypes?: LocationType[]
  placeId?: string
  skip?: boolean
}): { locations: ILocation[]; loading: boolean; error?: Error } {
  const { center, zoom, locationTypes, placeId, skip } = args

  // Deliberately center + zoom + locationTypes ONLY — no size band, no price
  // range, no sort. A directory row carries ONE unpriced configuration with no
  // `area`, so range filters match nothing, and only 21% of rows have a price
  // at all. The directory is a ZONE-level answer, never a filtered result set.
  const searchInput = {
    placeId,
    center: center ? { lat: center.lat, lng: center.lng } : undefined,
    zoom,
    locationTypes
  }
  const viewportKey = JSON.stringify(searchInput)

  const { data, error, loading } = useQuery<
    Pick<IQuery, 'directoryLocations'>,
    IQueryDirectoryLocationsArgs
  >(DIRECTORY_LOCATIONS, {
    // `locationTypes: []` means "this vertical publishes no directory rows"
    // (warehouse / nave / land) — asking for none would return everything.
    skip: skip || (!center && !placeId) || locationTypes?.length === 0,
    fetchPolicy: 'cache-first',
    variables: {
      pagination: { from: 0, pageSize: DIRECTORY_VIEWPORT_SIZE },
      searchInput
    }
  })

  const hits = data?.directoryLocations?.hits as ILocation[] | undefined
  const [held, setHeld] = useState<{ key: string; hits: ILocation[] } | null>(
    null
  )
  useEffect(() => {
    if (hits) setHeld({ key: viewportKey, hits })
  }, [hits, viewportKey])

  return {
    locations: hits ?? (held?.key === viewportKey ? held.hits : []),
    loading,
    error
  }
}
